import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Badge, Tooltip, Popover, List, ListItem, ListItemText, ListItemIcon, Typography, Box, Button } from '@mui/material';
import { NotificationsNone, Notifications, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { firestoreRestApi } from '../../utils/firestoreRestApi';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

interface NotificationData {
  id: string;
  title: string;
  body: string;
  timestamp: any;
  read: boolean;
  type: 'daily_reminder' | 'achievement' | 'team_update' | 'health_metrics_needed' | 'run_activity_needed';
  navigateTo?: string;
}

interface NotificationIndicatorProps {
  onPageChange: (page: string) => void;
}

const NotificationIndicator: React.FC<NotificationIndicatorProps> = ({ onPageChange }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState(true);

  const parseAndSetNotifications = (raw: any[]) => {
    let notificationsArray: any[] = [];
    if (Array.isArray(raw)) {
      notificationsArray = raw;
    } else if (raw && typeof raw === 'object') {
      notificationsArray = Object.values(raw).filter(
        (item: any) => item && typeof item === 'object' && item.id
      );
    }
    notificationsArray = notificationsArray.map(n => ({
      id: n.id || `notification_${Date.now()}`,
      title: n.title || 'Notification',
      body: n.body || '',
      timestamp: n.timestamp || new Date(),
      read: n.read || false,
      type: n.type || 'daily_reminder',
      navigateTo: n.navigateTo || 'activities'
    }));
    const sorted = notificationsArray.sort((a: any, b: any) => {
      const tA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
      const tB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
      return tB.getTime() - tA.getTime();
    });
    setNotifications(sorted);
    setLoading(false);
  };

  const fetchViaRestApi = useCallback(async () => {
    if (!user?.id) return;
    try {
      const restDoc = await firestoreRestApi.getDocument('users', user.id);
      if (restDoc) {
        parseAndSetNotifications(restDoc.notifications || []);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.warn('[NotificationIndicator] REST API fetch failed:', err);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const isNative = Capacitor.getPlatform() !== 'web';

    if (isNative) {
      // On iOS/Android the Firestore SDK silently hangs — bypass it entirely.
      // Seed immediately from AuthContext so the badge renders right away,
      // then fetch fresh data via REST API and keep polling every 30 s.
      parseAndSetNotifications((user as any)?.notifications || []);
      fetchViaRestApi();
      const interval = setInterval(fetchViaRestApi, 30000);
      return () => clearInterval(interval);
    }

    // Web: use real-time onSnapshot
    const notificationsRef = doc(db, 'users', user.id);
    const unsubscribe = onSnapshot(
      notificationsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          parseAndSetNotifications(snapshot.data().notifications || []);
        } else {
          setLoading(false);
        }
      },
      (error) => {
        console.warn('[NotificationIndicator] onSnapshot error, falling back to REST API:', error.message);
        fetchViaRestApi();
      }
    );
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, fetchViaRestApi]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const hasNotifications = notifications.length > 0;

  const clearIOSBadgeAndNotificationsOnOpen = () => {
    if (Capacitor.getPlatform() === 'ios') {
      PushNotifications.removeAllDeliveredNotifications().catch(() => {});
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    clearIOSBadgeAndNotificationsOnOpen();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: NotificationData) => {
    // Mark as read
    await markAsRead(notification.id);
    
    // Navigate if there's a navigation target
    if (notification.navigateTo) {
      onPageChange(notification.navigateTo);
      handleClose(); // Close the popover
    }
  };


  const prepareNotificationsForSave = (notifs: NotificationData[]) =>
    notifs.map(n => ({
      ...n,
      timestamp: n.timestamp?.toDate
        ? n.timestamp.toDate()
        : n.timestamp instanceof Date
        ? n.timestamp
        : new Date(n.timestamp),
    }));

  const markAsRead = async (notificationId: string) => {
    if (!user?.id) return;
    const updatedNotifications = notifications.map(n =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    // Optimistically update local state immediately
    setNotifications(updatedNotifications);

    const isNative = Capacitor.getPlatform() !== 'web';
    if (isNative) {
      try {
        await firestoreRestApi.setDocument('users', user.id, {
          notifications: prepareNotificationsForSave(updatedNotifications),
          updatedAt: new Date().toISOString(),
        }, true);
      } catch (err) {
        console.error('[NotificationIndicator] REST markAsRead failed:', err);
      }
      return;
    }

    try {
      await updateDoc(doc(db, 'users', user.id), {
        notifications: updatedNotifications,
        updatedAt: new Date(),
      });
    } catch (error: any) {
      console.error('[NotificationIndicator] SDK markAsRead failed:', error);
      try {
        await firestoreRestApi.setDocument('users', user.id, {
          notifications: prepareNotificationsForSave(updatedNotifications),
          updatedAt: new Date().toISOString(),
        }, true);
      } catch (restErr) {
        console.error('[NotificationIndicator] REST markAsRead fallback failed:', restErr);
      }
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;
    const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updatedNotifications);
    if (Capacitor.getPlatform() === 'ios') {
      PushNotifications.removeAllDeliveredNotifications().catch(() => {});
    }

    const isNative = Capacitor.getPlatform() !== 'web';
    if (isNative) {
      try {
        await firestoreRestApi.setDocument('users', user.id, {
          notifications: prepareNotificationsForSave(updatedNotifications),
          updatedAt: new Date().toISOString(),
        }, true);
      } catch (err) {
        console.error('[NotificationIndicator] REST markAllAsRead failed:', err);
      }
      return;
    }

    try {
      await updateDoc(doc(db, 'users', user.id), {
        notifications: updatedNotifications,
        updatedAt: new Date(),
      });
    } catch (error: any) {
      console.error('[NotificationIndicator] SDK markAllAsRead failed:', error);
      try {
        await firestoreRestApi.setDocument('users', user.id, {
          notifications: prepareNotificationsForSave(updatedNotifications),
          updatedAt: new Date().toISOString(),
        }, true);
      } catch (restErr) {
        console.error('[NotificationIndicator] REST markAllAsRead fallback failed:', restErr);
      }
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'daily_reminder':
        return '📝';
      case 'achievement':
        return '🏆';
      case 'team_update':
        return '👥';
      case 'health_metrics_needed':
        return '🏥';
      case 'run_activity_needed':
        return '🏃‍♂️';
      default:
        return '🔔';
    }
  };

  if (loading) {
    return (
      <IconButton color="inherit">
        <NotificationsNone />
      </IconButton>
    );
  }

  return (
    <>
      <Tooltip title={hasNotifications ? `${unreadCount} unread notifications` : 'No notifications'}>
        <IconButton 
          color="inherit" 
          onClick={handleClick}
          sx={{ position: 'relative' }}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            invisible={unreadCount === 0}
            max={99}
          >
            {hasNotifications ? <Notifications /> : <NotificationsNone />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPopover-paper': {
            width: 360,
            maxHeight: 400,
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={markAllAsRead}>
                Mark all read
              </Button>
            )}
          </Box>
        </Box>

        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {notifications.map((notification, index) => (
                      <ListItem
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          backgroundColor: notification.read ? 'transparent' : 'action.hover',
                          borderLeft: notification.read ? 'none' : '4px solid',
                          borderLeftColor: 'primary.main',
                          cursor: notification.navigateTo ? 'pointer' : 'default',
                          '&:hover': notification.navigateTo ? {
                            backgroundColor: 'action.selected',
                          } : {},
                        }}
                      >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Typography variant="h6">
                    {getNotificationIcon(notification.type)}
                  </Typography>
                </ListItemIcon>
                <ListItemText
                  primary={notification.title}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.secondary" display="block">
                        {notification.body}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {formatTime(notification.timestamp)}
                      </Typography>
                    </>
                  }
                />
                {!notification.read && (
                  <IconButton
                    size="small"
                    onClick={() => markAsRead(notification.id)}
                    sx={{ ml: 1 }}
                  >
                    <CheckCircle fontSize="small" color="primary" />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationIndicator;
